import { useCallback, useState, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { Device as CapacitorDevice } from '@capacitor/device';
import { storageSvc } from '@/services/storageServiceClient';
import { STORAGE_KEYS } from '@/utils/constants';
import { Store, Device } from '@/libs/onboarding/types';
import { PAIR_DEVICE } from '../queries/devices';
import {
  PairDeviceReqInput,
  Device as OrgDevice,
  DeviceMeta,
} from '@oolio-group/domain';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { APP_CONFIG } from '@/config';

type FingerprintJSAgent = Awaited<
  ReturnType<typeof import('@fingerprintjs/fingerprintjs').default.load>
>;
type UAParserType = typeof import('ua-parser-js').UAParser;

interface DevicePairingRequestInput {
  input: PairDeviceReqInput;
}

interface DevicePairingResult {
  pairDevice: Pick<
    OrgDevice,
    'id' | 'name' | 'isPaired' | 'config' | 'deviceMetadata'
  >;
}

interface UseDevicePairingReturn {
  isDevicePairing: boolean;
  pairingError: string | null;
  pairDevice: (
    store: Store,
    device: Device,
  ) => Promise<{ success: boolean; storeId: string; deviceId: string }>;
  clearError: () => void;
  getStoredFingerprint: () => Promise<string | null>;
  storeFingerprint: (deviceUuid: string) => Promise<void>;
}

export const useDevicePairing = (): UseDevicePairingReturn => {
  const [isDevicePairing, setIsDevicePairing] = useState(false);
  const [pairingError, setPairingError] = useState<string | null>(null);

  // Memoized FingerprintJS agent to avoid reloading on retries
  const fingerprintAgentRef = useRef<FingerprintJSAgent | null>(null);

  const [pairDeviceMutation] = useMutation<
    DevicePairingResult,
    DevicePairingRequestInput
  >(PAIR_DEVICE);

  // Lazy loading helper functions
  const getFingerprintAgent =
    useCallback(async (): Promise<FingerprintJSAgent> => {
      if (fingerprintAgentRef.current) {
        return fingerprintAgentRef.current;
      }

      const FingerprintJS = await import('@fingerprintjs/fingerprintjs');
      const agent = await FingerprintJS.default.load();
      fingerprintAgentRef.current = agent;
      return agent;
    }, []);

  const loadUAParser = useCallback(async (): Promise<UAParserType> => {
    const module = await import('ua-parser-js');
    return module.UAParser;
  }, []);

  const generateFingerprint = useCallback(async (): Promise<string> => {
    const fp = await getFingerprintAgent();
    const result = await fp.get();
    return result.visitorId;
  }, [getFingerprintAgent]);

  const generateDeviceMetadata = useCallback(async (): Promise<{
    deviceUuid: string;
    deviceMetadata: DeviceMeta;
  }> => {
    try {
      const deviceUuid = await generateFingerprint();

      const deviceInfo = await CapacitorDevice.getInfo();

      // Lazy load UA Parser and parse user agent
      const UAParser = await loadUAParser();
      const parser = new UAParser(navigator.userAgent);
      const uaBrowser = parser.getBrowser();
      const uaOS = parser.getOS();

      let appVersion;
      const isNativePlatform = Capacitor.getPlatform() !== 'web';
      if (isNativePlatform) {
        const appInfo = await App.getInfo();
        appVersion = appInfo.version;
      } else {
        appVersion = APP_CONFIG.VITE_ORB_APP_VERSION;
      }

      const deviceMetadata: DeviceMeta = {
        // Webkit 605, Chrome WebView 138, Chrome 139, Edge 139
        browser: `${uaBrowser.name} ${uaBrowser.major || uaBrowser.version}`,
        // iOS 18.5 android 15, macos 10.15.7, windows 10
        os: `${uaOS.name} ${uaOS.version}`,
        // iOS, android ,web, web
        platform: deviceInfo.platform,
        appVersion,
        // iPad (A 16), Pixel tablet, Macintosh, Windows NT
        deviceType: deviceInfo.model || 'Unknown',
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        userAgent: navigator.userAgent,
      };
      return { deviceUuid, deviceMetadata };
    } catch (error) {
      console.error('Error generating device metadata:', error);
      throw new Error('Failed to collect device information');
    }
  }, [generateFingerprint, loadUAParser]);

  const storeFingerprint = useCallback(
    async (deviceUuid: string): Promise<void> => {
      try {
        await storageSvc.store(STORAGE_KEYS.FINGERPRINT_UUID, deviceUuid);
      } catch (error) {
        console.error('Error storing fingerprint UUID:', error);
        throw new Error('Failed to store fingerprint UUID');
      }
    },
    [],
  );

  const pairDevice = useCallback(
    async (
      store: Store,
      device: Device,
    ): Promise<{ success: boolean; storeId: string; deviceId: string }> => {
      try {
        setIsDevicePairing(true);
        setPairingError(null);

        // Generate device UUID and metadata
        const { deviceUuid, deviceMetadata } = await generateDeviceMetadata();

        // Store fingerprint UUID persistently
        await storeFingerprint(deviceUuid);

        // Call device pairing mutation
        const result = await pairDeviceMutation({
          variables: {
            input: {
              deviceId: device.id,
              deviceUuid: deviceUuid,
              deviceMetadata: deviceMetadata,
            },
          },
        });

        if (result.data?.pairDevice.isPaired) {
          return {
            success: true,
            storeId: store.id,
            deviceId: device.id,
          };
        } else {
          throw new Error('Device pairing was not successful');
        }
      } catch (error) {
        console.error('Device pairing error:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Device pairing failed';
        setPairingError(errorMessage);

        // Clean up stored UUID on failure
        try {
          await storageSvc.remove(STORAGE_KEYS.FINGERPRINT_UUID);
        } catch (cleanupError) {
          console.error('Error cleaning up fingerprint UUID:', cleanupError);
        }

        return {
          success: false,
          storeId: store.id,
          deviceId: device.id,
        };
      } finally {
        setIsDevicePairing(false);
      }
    },
    [generateDeviceMetadata, pairDeviceMutation, storeFingerprint],
  );

  const clearError = useCallback(() => {
    setPairingError(null);
  }, []);

  const getStoredFingerprint = useCallback(async (): Promise<string | null> => {
    try {
      const storedFingerprint = await storageSvc.retrieve(
        STORAGE_KEYS.FINGERPRINT_UUID,
      );

      if (storedFingerprint) {
        return storedFingerprint;
      }

      // No stored fingerprint, generate a new one using the shared function
      const deviceUuid = await generateFingerprint();

      // Store the new fingerprint
      await storeFingerprint(deviceUuid);

      return deviceUuid;
    } catch (error) {
      console.error('Error retrieving or generating fingerprint:', error);
      return null;
    }
  }, [generateFingerprint, storeFingerprint]);

  return {
    isDevicePairing,
    pairingError,
    pairDevice,
    clearError,
    getStoredFingerprint,
    storeFingerprint,
  };
};
